import type { Effect, ResourceBundle } from '../engine';
import { JOBS_BY_ID, RESOURCES_BY_ID, TECHS_BY_ID, formatNumber, formatPercent } from '../engine';

function resourceName(id: string): string {
  return RESOURCES_BY_ID[id]?.name ?? id;
}

/** One-line, player-facing description of a single effect. */
export function describeEffect(effect: Effect, level = 1): string {
  const scaled = (amount: number): number => amount * level;

  switch (effect.kind) {
    case 'globalMult':
      return `${formatPercent(scaled(effect.amount))} all production`;
    case 'resourceMult':
      return `${formatPercent(scaled(effect.amount))} ${resourceName(effect.resource)}`;
    case 'jobMult':
      return `${formatPercent(scaled(effect.amount))} ${JOBS_BY_ID[effect.job]?.name ?? effect.job}`;
    case 'flatProduction':
      return `+${formatNumber(scaled(effect.amount))} ${resourceName(effect.resource)}/s`;
    case 'storage':
      return `+${formatNumber(scaled(effect.amount))} ${resourceName(effect.resource)} storage`;
    case 'storageMult':
      return effect.resource === '*'
        ? `${formatPercent(scaled(effect.amount))} all storage`
        : `${formatPercent(scaled(effect.amount))} ${resourceName(effect.resource)} storage`;
    case 'shelter':
      return `+${formatNumber(scaled(effect.amount))} shelter`;
    case 'nutritionMult':
      return `${formatPercent(scaled(effect.amount))} nutrition`;
    case 'consumptionMult':
      return `${formatPercent(scaled(effect.amount))} food eaten`;
    case 'growthMult':
      return `${formatPercent(scaled(effect.amount))} growth`;
    case 'akGainMult':
      return `${formatPercent(scaled(effect.amount))} Ancestral Knowledge`;
    case 'vigorMult':
      return `${formatPercent(scaled(effect.amount))} Vigor strength`;
    case 'vigorStacks':
      return `+${formatNumber(scaled(effect.amount))} max Vigor`;
    case 'offlineHours':
      return `+${formatNumber(scaled(effect.amount))}h away time`;
    case 'taleLuck':
      return `${formatPercent(scaled(effect.amount))} good omens`;
    case 'startPopulation':
      return `+${formatNumber(scaled(effect.amount))} starting tribe`;
    case 'startResource':
      return `Start with ${formatNumber(scaled(effect.amount))} ${resourceName(effect.resource)}`;
    case 'startTech':
      return `Start knowing ${TECHS_BY_ID[effect.tech]?.name ?? effect.tech}`;
  }
}

export interface CostLine {
  resourceId: string;
  name: string;
  amount: number;
  affordable: boolean;
}

export function costLines(cost: ResourceBundle, held: Record<string, number>): CostLine[] {
  return Object.entries(cost)
    .filter(([, amount]) => (amount ?? 0) > 0)
    .map(([resourceId, amount]) => ({
      resourceId,
      name: resourceName(resourceId),
      amount: amount ?? 0,
      affordable: (held[resourceId] ?? 0) >= (amount ?? 0) - 1e-6,
    }));
}
